import React, { createContext, useContext, useEffect, useState } from 'react'
import { authApiService } from '../services/authApiService'
import type { User } from '../types'

interface AuthContextValue {
  user: User | null
  loading: boolean
  login: (email: string, password: string, turnstileToken?: string) => Promise<{ mfaRequired: boolean; emailVerificationRequired: boolean }>
  logout: () => Promise<void>
  refresh: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  async function loadUser() {
    try {
      const me = await authApiService.me()
      setUser(me)
    } catch {
      setUser(null)
    }
  }

  useEffect(() => {
    loadUser().finally(() => setLoading(false))
  }, [])

  async function login(email: string, password: string, turnstileToken?: string) {
    const res = await authApiService.login({ emailAddress: email, password, turnstileToken })
    if (res?.requiresEmailVerification) {
      return { mfaRequired: false, emailVerificationRequired: true }
    }
    if (res?.requiresMfa) {
      return { mfaRequired: true, emailVerificationRequired: false }
    }
    await loadUser()
    return { mfaRequired: false, emailVerificationRequired: false }
  }

  async function logout() {
    try {
      await authApiService.logout()
    } catch {}
    Object.keys(sessionStorage)
      .filter(k => k.startsWith('msal.'))
      .forEach(k => sessionStorage.removeItem(k))
    setUser(null)
  }

  async function refresh() {
    await loadUser()
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
